<template>
  <div class="f-row">
    <button
      ref="toggleButtonRef"
      @click="toggleNotesDisplay"
      class="button-style textColor btn-add3 tr-all-03 op-50-hover gap-2 z-1000"
    >
      {{ currentPath }}
      <component
        :is="showNotes ? XIcon : MenuIcon"
        :size="32"
        :stroke-width="2"
        :stroke-linecap="0"
        class="icon-nav"
      />
    </button>

    <div
      ref="notesListRef"
      class="notes-list z-500 difference"
      v-show="showNotes || animating"
      :class="{ 'notes-visible': showNotes }"
      @click.stop
    >
      <router-link
        v-for="note in filteredNotes"
        :key="note.id"
        :to="`/${note.title}`"
        class="note-item button-style btn-add3 fade-in-blur"
        :class="{ active: isActiveNote(note) }"
      >
        <span @click="ui.showNotes = false"
          >/{{ note.title || 'Untitled Note' }}</span
        >
      </router-link>
    </div>
  </div>
</template>

<script setup>
// src/components/navigation/MainInfo.vue
import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useNotes } from '@/composables/usePocketNotes'
import anime from 'animejs'
import { MenuIcon, XIcon } from 'lucide-vue-next'
import { useUiState } from '@/stores/useUiState'

const route = useRoute()
const currentPath = ref(route.path)
const { notes, fetchNotes } = useNotes()
const ui = useUiState()
const showNotes = computed(() => ui.showNotes) // ✅ no .value here
const toggleButtonRef = ref(null)
const notesListRef = ref(null)
const animating = ref(false)
const openLock = ref(false)
let isMounted = false

const toggleNotesDisplay = () => {
  if (openLock.value || animating.value) return

  openLock.value = true

  if (!ui.showNotes) {
    ui.closeAll() // 👈 close any other open UI
    ui.showNotes = true
    fetchNotes()
  } else {
    ui.showNotes = false
  }

  setTimeout(() => {
    openLock.value = false
  }, 500)
}

// SAFELY HANDLE OUTSIDE CLICKS
const handleClickOutside = (event) => {
  const container = notesListRef.value
  const button = toggleButtonRef.value

  if (
    !container ||
    !ui.showNotes ||
    animating.value ||
    container.contains(event.target) ||
    button?.contains(event.target)
  ) {
    return
  }

  ui.showNotes = false
}

// Computed property to filter notes
const filteredNotes = computed(() => {
  return notes.value
    .filter((note) => note.list === true)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
})

// Function to check if a note is active
const isActiveNote = (note) => {
  return `/${note.title}` === currentPath.value
}

watch(
  () => route.path,
  (newPath) => {
    currentPath.value = newPath
  },
)

const addGlobalListeners = () => {
  if (!isMounted) {
    window.addEventListener('scroll', handleScroll)
    document.addEventListener('pointerdown', handleClickOutside)
    isMounted = true
  }
}

const removeGlobalListeners = () => {
  if (isMounted) {
    window.removeEventListener('scroll', handleScroll)
    document.removeEventListener('pointerdown', handleClickOutside)
    isMounted = false
  }
}

const handleScroll = () => {
  if (ui.showNotes && !animating.value) {
    ui.showNotes = false
  }
}

// Lifecycle
onMounted(() => {
  addGlobalListeners()
})

onUnmounted(() => {
  removeGlobalListeners()
})

// ANIMEJS WATCHER
watch(
  () => ui.showNotes,
  (visible) => {
    if (!notesListRef.value) return

    requestAnimationFrame(() => {
      animating.value = true

      const container = notesListRef.value
      const items = container.querySelectorAll('.note-item')

      if (visible) {
        anime.set(container, {
          opacity: 0,
          scale: 0.98,
          translateY: 20,
        })

        anime({
          targets: container,
          opacity: 1,
          scale: 1,
          translateY: 0,
          duration: 280,
          easing: 'easeOutQuad',
          begin: () => {
            container.style.display = 'block'
          },
          complete: () => {
            animating.value = false
          },
        })

        anime({
          targets: items,
          opacity: [0, 1],
          delay: anime.stagger(40, { start: 100 }),
          duration: 200,
          easing: 'easeOutCubic',
        })
      } else {
        anime({
          targets: container,
          opacity: 0,
          scale: 0.96,
          translateY: -10,
          duration: 220,
          easing: 'easeInCubic',
          complete: () => {
            animating.value = false

            if (visible) {
              setTimeout(() => {
                document.addEventListener('pointerdown', handleClickOutside)
              }, 10)
            } else {
              document.removeEventListener('pointerdown', handleClickOutside)
              anime.set(container, { display: 'none' })
            }
          },
        })
      }
    })
  },
)
</script>

<style scoped>
.notes-list {
  pointer-events: none;
  opacity: 0;
  transform-origin: top center;
  position: absolute;
  padding-top: 20vh;
  top: 0;
  left: 0;
  bottom: var(--main-padding);
  right: 0;
  height: calc(60vh - var(--main-padding) * 2);
  backdrop-filter: blur(2px);
  background-color: var(--base-color10);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: none;
  border-radius: 3px;

  padding-left: 0.5rem;
}

.notes-visible {
  pointer-events: auto;
}

.note-item {
  text-decoration: none;
  font-size: var(--font-size-L);
  width: fit-content;
  margin: 0.25rem;
  margin-bottom: 1rem;
  align-items: center !important;
}

.note-item:hover {
  opacity: 0.4 !important;
}

.active {
  font-weight: bold;
}
</style>
